import React from 'react';
import {
  Box, Divider, Button, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
} from '@mui/material';
import moment from 'moment';

import SubHeadingTitle from '../../components/layout/SubHeadingTitle';
import SubmissionAggregation from '../../components/submissionAggregation/SubmissionAggregation';
import {
  AggregatedSubmissionData,
  DigitalCase,
  DigitalGroundTruthGroup,
  DigitalQuestion,
  DigitalSection,
  QuestionCaseSetting,
} from '../../redux/types';
import ApiClient from '../../loopback/apiClient';
import { downloadExcelSheets } from '../../components/utilities/ExcelExport';

type Props = {
  selectedSection?: DigitalSection;
  digitalQuestions: DigitalQuestion[];
  digitalCases: DigitalCase[];
  groundTruthGroups: DigitalGroundTruthGroup[];
  selectedGroundTruthGroup: DigitalGroundTruthGroup | null;
  setSelectedGroundTruthGroup: (selectedGroup: number | null) => void;
  aggregatedSubmission?: AggregatedSubmissionData;
  settingsDictionary?: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
  trialCode: string;
};
const AggregatedTabPanel: React.FC<Props> = ({
  selectedSection,
  digitalQuestions: questions,
  digitalCases: cases,
  aggregatedSubmission,
  settingsDictionary,
  groundTruthGroups,
  selectedGroundTruthGroup,
  setSelectedGroundTruthGroup,
  trialCode,
}: Props) => {
  const selectedGroupIdExists = () => !!groundTruthGroups
    .find((g) => g.id === selectedGroundTruthGroup?.id);

  const onGroundTruthIdChange = (e: SelectChangeEvent) => {
    const selectedVal = e.target.value as string;
    setSelectedGroundTruthGroup(Number(selectedVal));
  };

  return (
    <>
      <Box
        aria-label="submission-aggregation-container"
        sx={{
          margin: '0 auto',
          px: 6,
          my: 3,
        }}
      >
        <SubHeadingTitle title="Submission Aggregations" />
        <Box display="flex" sx={{ my: 1 }}>
          <FormControl size="small" sx={{ width: 150 }}>
            <InputLabel id="groundtruth-group-label">GroundTruth ID</InputLabel>
            <Select
              labelId="groundtruth-group-label"
              id="groundtruth-id-select"
              value={selectedGroupIdExists() ? selectedGroundTruthGroup?.id.toString() || '' : ''}
              label="GroundTruth ID"
              onChange={onGroundTruthIdChange}
            >
              {
                groundTruthGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.index}</MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Box>
        <Box aria-label="question-editor" sx={{ my: 2, py: 3 }}>
          <SubmissionAggregation
            selectedSectionId={selectedSection?.id}
            digitalQuestions={questions}
            digitalCases={cases}
            aggregatedSubmission={aggregatedSubmission}
            settingsDictionary={settingsDictionary}
          />
        </Box>
      </Box>
      <Divider />
      <Box
        aria-label="buttons-container"
        display="flex"
        gap={2}
        sx={{
          px: 6,
          my: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            if (!selectedSection?.digitalSurveyId || !selectedGroundTruthGroup?.id) {
              return;
            }
            ApiClient.exportAggregatedSubmission(
              selectedSection?.digitalSurveyId, selectedGroundTruthGroup?.id,
            ).then((data) => downloadExcelSheets(data, `AG-${trialCode}-${moment().format('DDMMYY')}-GT${selectedGroundTruthGroup?.index}`));
          }}
        >
          Export
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (!selectedSection?.digitalSurveyId) {
              return;
            }
            ApiClient.exportCtxAggregatedSubmission(selectedSection?.digitalSurveyId)
              .then((data) => downloadExcelSheets(data, `CAG-${trialCode}-${moment().format('DDMMYY')}`));
          }}
        >
          Export Context
        </Button>
      </Box>
      <Box
        aria-label="buttons-container"
        display="flex"
        gap={2}
        sx={{
          px: 6,
          my: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            if (!selectedSection?.digitalSurveyId || !selectedGroundTruthGroup?.id) {
              return;
            }
            ApiClient.exportAggregatedSubmission(
              selectedSection?.digitalSurveyId, selectedGroundTruthGroup?.id, true,
            ).then((data) => downloadExcelSheets(data, `AG-${trialCode}-${moment().format('DDMMYY')}-GT${selectedGroundTruthGroup?.index}-F`));
          }}
        >
          Export Finalized
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (!selectedSection?.digitalSurveyId) {
              return;
            }
            ApiClient.exportCtxAggregatedSubmission(selectedSection?.digitalSurveyId, true)
              .then((data) => downloadExcelSheets(data, `CAG-${trialCode}-${moment().format('DDMMYY')}-F`));
          }}
        >
          Export Finalized Context
        </Button>
      </Box>
    </>
  );
};

export default AggregatedTabPanel;
